import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { idToUri } from 'pages/global/UriHelper';
import { entityTypes } from 'pages/translation/config/Constants';
import queryString from 'query-string';

import baseAPI from '../../api/ApiConnector_v2';
import { getEnv } from '../../config/Env';
import { baseQueryWithReauth, isJsonContentType, prepareHeaders, providesList } from '../api';

const baseUrl = getEnv('PUBLICATION_API_URL');
const uriPrefix = '/api';

const publicationApiQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
    isJsonContentType,
    responseHandler: 'content-type',
});

export const publicationApi = createApi({
    reducerPath: 'publicationApi',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, publicationApiQuery),
    tagTypes: [
        'Publication',
        'PublicationUser',
        'UserGroup',
        'PublicationGroup',
        'PublicationVersion',
        'OpListExportToken',
        'PublicationPreview',
        'PublicationType',
    ],
    endpoints: (builder) => ({
        addPropositionSummaryToken: builder.mutation({
            query: (body) => {
                return {
                    url: `${uriPrefix}/proposition_summary_tokens`,
                    method: 'POST',
                    body,
                };
            },
        }),
        addPublicationUserPassword: builder.mutation({
            query: (body) => {
                return {
                    url: `${uriPrefix}/publication_user_passwords`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: [{ type: 'PublicationUserPassword', id: 'LIST' }],
        }),
        getPublicationUserPassword: builder.query({
            query: (uri) => `${uri}/publication_user_passwords`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'PublicationUserPassword'),
        }),
        deletePublicationUserPassword: builder.mutation({
            query: (uri) => {
                return {
                    url: uri,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [{ type: 'PublicationUserPassword', id: 'LIST' }],
        }),
        putForBatch: builder.mutation({
            query: ({ uri, body }) => {
                return {
                    url: uri,
                    method: 'PUT',
                    body,
                };
            },
        }),
        postForBatch: builder.mutation({
            query: ({ uri, body }) => {
                return {
                    url: uri,
                    method: 'POST',
                    body,
                };
            },
        }),
        deleteForBatch: builder.mutation({
            query: (uri) => {
                return {
                    url: uri,
                    method: 'DELETE',
                };
            },
        }),
        getPublicationUserPasswords: builder.query({
            query: (id) => `${uriPrefix}/publications/${id}/publication_user_passwords`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'PublicationUserPassword'),
        }),
        addPublicationUser: builder.mutation({
            query: (body) => {
                return {
                    url: `${uriPrefix}/publication_users`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: [
                { type: 'PublicationUser', id: 'LIST' },
                { type: 'UserGroup', id: 'LIST' },
            ],
        }),
        addUserGroup: builder.mutation({
            query: (body) => {
                return {
                    url: `${uriPrefix}/user_groups`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: [{ type: 'UserGroup', id: 'LIST' }],
        }),
        deletePublicationUser: builder.mutation({
            query: (uri) => {
                return {
                    url: uri,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [
                { type: 'PublicationUser', id: 'LIST' },
                { type: 'UserGroup', id: 'LIST' },
            ],
        }),
        deleteUserGroup: builder.mutation({
            query: (uri) => {
                return {
                    url: uri,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [
                { type: 'PublicationUser', id: 'LIST' },
                { type: 'UserGroup', id: 'LIST' },
            ],
        }),
        editPublicationUser: builder.mutation({
            query: ({ uri, body }) => {
                return {
                    url: uri,
                    method: 'PUT',
                    body,
                };
            },
            invalidatesTags: [
                { type: 'PublicationUser', id: 'LIST' },
                { type: 'UserGroup', id: 'LIST' },
            ],
        }),
        editPublicationUserPassword: builder.mutation({
            query: ({ uri, body }) => {
                return {
                    url: uri,
                    method: 'PUT',
                    body,
                };
            },
            invalidatesTags: (result, error, arg) => [{ type: 'PublicationUserPassword', id: arg.uri }],
        }),
        editUserGroup: builder.mutation({
            query: ({ uri, body }) => {
                return {
                    url: uri,
                    method: 'PUT',
                    body,
                };
            },
            invalidatesTags: [
                { type: 'PublicationUser', id: 'LIST' },
                { type: 'UserGroup', id: 'LIST' },
            ],
        }),
        getUserGroups: builder.query({
            query: () => `${uriPrefix}/user_groups`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'UserGroup'),
        }),
        getPublications: builder.query({
            query: () => `${uriPrefix}/publications`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'Publication'),
            keepUnusedDataFor: 900, // keep data in cache for 15 minutes
        }),
        getPublication: builder.query({
            query: (id) => `${uriPrefix}/publications/${id}`,
            providesTags: (result) => [{ type: 'Publication', id: result['@id'] }],
        }),
        getPublicationVersion: builder.query({
            query: (uri) => uri,
            providesTags: (result) => [{ type: 'PublicationVersion', id: result['@id'] }],
        }),
        getPublicationUrls: builder.query({
            query: ({ id, preview }) => `${uriPrefix}/publications/${id}/urls${preview ? '?preview=1' : ''}`,
            providesTags: (result, error, { id }) => [{ type: 'Publication', id: `/api/publications/${id}` }],
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
        getVersionUrls: builder.query({
            query: (uri) => `${uri}/urls`,
            providesTags: (result, error, uri) => [{ type: 'PublicationVersion', id: uri }],
            keepUnusedDataFor: 3600, // keep data in cache for 1 hour
        }),
        editPublication: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body,
            }),
            invalidatesTags: (result) => [{ type: 'Publication', id: result['@id'] }],
        }),
        editPublicationVersion: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body,
            }),
            invalidatesTags: (result) => [{ type: 'PublicationVersion', id: result['@id'] }],
        }),
        lockPublicationVersion: builder.mutation({
            query: (uri) => ({
                url: `${uri}/lock`,
                method: 'POST',
                headers: { 'Content-Type': 'application/ld+json' },
                body: {},
            }),
            invalidatesTags: (result) => [{ type: 'PublicationVersion', id: result['@id'] }],
        }),
        addPublication: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/publications`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Publication', id: 'LIST' }],
        }),
        publishPublication: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/publication_tokens`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => [{ type: 'Publication', id: result['@id'] }],
        }),
        batchPublishPublications: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/batch_publication_tokens`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => [{ type: 'PublicationVersion', id: 'LIST' }],
        }),
        deletePublications: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/batch_deletes`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'Publication', id: 'LIST' }],
        }),
        deletePublicationVersion: builder.mutation({
            query: ({ uri, publicationUri, force = false }) => ({
                url: uri + (force ? '?force=true' : ''),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Publication', id: arg.publicationUri }],
        }),
        getPublicationPreviewTokens: builder.query({
            query: (uri) => `${uri}/publication_preview_tokens`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result, error, arg) => [
                {
                    type: 'PublicationPreview',
                    id: arg,
                },
            ],
        }),
        createPreviewToken: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/publication_preview_tokens`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => [{ type: 'PublicationPreview', id: result.publication }],
        }),
        createOpListExportToken: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/op_list_export_tokens`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'OpListExportToken', id: 'LIST' }],
        }),
        getOpListExportTokens: builder.query({
            query: (opDepartmentId) => `${uriPrefix}/op_list_export_tokens?opDepartmentId=${opDepartmentId}`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'OpListExportToken'),
        }),
        getDocumentVariantPublications: builder.query({
            query: (variantIds = []) =>
                `${uriPrefix}/publications/document-variants?` +
                queryString.stringify(
                    {
                        variant: variantIds,
                    },
                    { arrayFormat: 'bracket' },
                ),
            transformResponse: (response) => response['hydra:member'],
        }),
        getPublicationGroups: builder.query({
            query: () => `${uriPrefix}/publication_groups`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'PublicationGroup'),
            keepUnusedDataFor: 3600, // keep data in memory for 1 hour
        }),
        createPublicationGroup: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/publication_groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'PublicationGroup', id: 'LIST' }],
        }),
        deletePublicationGroup: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'PublicationGroup', id: 'LIST' }],
        }),
        editPublicationGroup: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result) => [{ type: 'PublicationGroup', id: result['@id'] }],
        }),
        editPublicationGroups: builder.mutation({
            query: (changes) => ({
                url: `${uriPrefix}/batch_edits`,
                method: 'POST',
                body: {
                    publicationGroups: changes,
                },
            }),
            invalidatesTags: () => [{ type: 'PublicationGroup', id: 'LIST' }],
            async onQueryStarted(body, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    publicationApi.util.updateQueryData('getPublicationGroups', undefined, (draft) => {
                        draft.forEach((_group) => {
                            const groupPatch = body.find((patch) => patch.id === _group.id);

                            if (groupPatch) {
                                Object.assign(_group, {
                                    ..._group,
                                    ...groupPatch.changes,
                                });
                            }
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        editPublications: builder.mutation({
            query: (changes) => ({
                url: `${uriPrefix}/batch_edits`,
                method: 'POST',
                body: {
                    publications: changes,
                },
            }),
            invalidatesTags: () => [
                { type: 'Publication', id: 'LIST' },
                { type: 'PublicationGroup', id: 'LIST' },
            ],
            async onQueryStarted(body, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    publicationApi.util.updateQueryData('getPublications', undefined, (draft) => {
                        draft.forEach((publication) => {
                            const publicationPatch = body.find((patch) => patch.id === publication.id);

                            if (publicationPatch) {
                                Object.assign(publication, {
                                    ...publication,
                                    ...publicationPatch.changes,
                                });
                            }
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getPublicationUsers: builder.query({
            query: () => `${uriPrefix}/publication_users`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'PublicationUser'),
        }),
        getUserPublicationPasswords: builder.query({
            query: (id) => `${uriPrefix}/publication_users/${id}/publication_user_passwords`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'PublicationUserPassword'),
        }),
        getUserPasswordActions: builder.query({
            query: (uri) => `${uri}/user_password_actions`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result, error, arg) => [{ type: 'PublicationUserPassword', id: arg }],
        }),
        refreshKioskData: builder.mutation({
            query: (body) => {
                return {
                    url: `${uriPrefix}/publications/refresh-kiosk-data`,
                    method: 'POST',
                    body,
                };
            },
        }),
        updateToken: builder.mutation({
            query: (body) => {
                return {
                    url: `${uriPrefix}/publication_tokens/update`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, arg) => [
                {
                    type: 'Publication',
                    id: idToUri(arg.publicationId, entityTypes.PUBLICATION),
                },
            ],
        }),
        addExternalVersion: builder.mutation({
            query: ({ publicationUri, body }) => {
                return {
                    url: `${uriPrefix}/publication_versions/external-version`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (result, error, arg) => [
                {
                    type: 'Publication',
                    id: arg.publicationUri,
                },
            ],
        }),
        getPublicationVersions: builder.query({
            query: (id) => `${uriPrefix}/publications/${id}/publication_versions`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result, error, arg) =>
                [
                    {
                        type: 'Publication',
                        id: `/api/publications/${arg}`,
                    },
                ].concat(providesList(result, 'PublicationVersion')),
        }),
        generatePublicationVersionHashes: builder.mutation({
            query: (uri) => {
                return {
                    url: `${uri}/hashes`,
                    method: 'POST',
                    body: {},
                };
            },
            invalidatesTags: (result, error, arg) => [
                {
                    type: 'PublicationVersion',
                    id: arg,
                },
            ],
        }),
        getPublicationTypes: builder.query({
            query: () => `${uriPrefix}/publication_types`,
            transformResponse: (response) => response['hydra:member'],
            providesTags: (result) => providesList(result, 'PublicationType'),
        }),
        getPublicationType: builder.query({
            query: (uri) => uri,
            providesTags: (result) => [{ type: 'PublicationType', id: result['@id'] }],
        }),
        editPublicationType: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result, error, { uri }) => [
                { type: 'PublicationType', id: result?.['@id'] ?? uri },
                { type: 'PublicationType', id: 'LIST' },
            ],
            async onQueryStarted({ uri, body }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    publicationApi.util.updateQueryData('getPublicationTypes', undefined, (draft) => {
                        if (!Array.isArray(draft)) {
                            return;
                        }

                        const publicationType = draft.find((type) => type['@id'] === uri);

                        if (publicationType) {
                            Object.assign(publicationType, body);
                        }
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        addPublicationType: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/publication_types`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'PublicationType' }],
        }),
        getUniqueMetaCheck: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/unique-meta-check`,
                method: 'POST',
                body,
            }),
        }),
    }),
});

export function downloadVersionFile(uri, fileType) {
    return baseAPI.post(`${baseUrl}${uri}/download/${fileType}`, undefined, {
        responseType: 'blob',
    });
}

export const {
    useAddPropositionSummaryTokenMutation,
    useAddPublicationUserPasswordMutation,
    useGetPublicationUserPasswordQuery,
    useAddPublicationUserMutation,
    useAddUserGroupMutation,
    useDeletePublicationUserMutation,
    useDeletePublicationUserPasswordMutation,
    useDeleteUserGroupMutation,
    useEditPublicationUserMutation,
    useEditPublicationUserPasswordMutation,
    useEditUserGroupMutation,
    useGetUserGroupsQuery,
    useGetPublicationsQuery,
    useGetPublicationQuery,
    useEditPublicationMutation,
    useAddPublicationMutation,
    usePublishPublicationMutation,
    useDeletePublicationVersionMutation,
    useDeletePublicationsMutation,
    useGetPublicationPreviewTokensQuery,
    useCreatePreviewTokenMutation,
    useGetDocumentVariantPublicationsQuery,
    useGetPublicationUsersQuery,
    useGetUserPublicationPasswordsQuery,
    useGetPublicationGroupsQuery,
    useCreatePublicationGroupMutation,
    useDeletePublicationGroupMutation,
    useEditPublicationGroupMutation,
    useEditPublicationGroupsMutation,
    useEditPublicationsMutation,
    useGetUserPasswordActionsQuery,
    useGetPublicationUserPasswordsQuery,
    useRefreshKioskDataMutation,
    useUpdateTokenMutation,
    useDeleteForBatchMutation,
    usePutForBatchMutation,
    usePostForBatchMutation,
    useGetPublicationVersionsQuery,
    useGetPublicationVersionQuery,
    useBatchPublishPublicationsMutation,
    useCreateOpListExportTokenMutation,
    useGetOpListExportTokensQuery,
    useGeneratePublicationVersionHashesMutation,
    useAddExternalVersionMutation,
    useGetPublicationTypesQuery,
    useGetPublicationTypeQuery,
    useEditPublicationTypeMutation,
    useAddPublicationTypeMutation,
    useEditPublicationVersionMutation,
    useLockPublicationVersionMutation,
    useGetUniqueMetaCheckMutation,
    useGetPublicationUrlsQuery,
    useGetVersionUrlsQuery,
} = publicationApi;
